// src/components/Navbar.js - VERSION FINALE (Mise à jour instantanée Login/Logout + Badge + Marché + Prêt + Obligations)

import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ThemeToggleButton from './ThemeToggleButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// 🆕 AJOUT DE faStore, faHandHoldingUsd et faFileInvoiceDollar
import { faRobot, faBell, faSignOutAlt, faHome, faUser, faTrophy, faChartLine, faHistory, faTimes, faBars, faStore, faHandHoldingUsd, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../ThemeContext';
import { useSocket } from '../SocketContext';
import { performLogout } from '../utils/logout';
import { Modal } from 'react-bootstrap';
import './Navbar.css';
import './LogoutModal.css';

// 🆕 IMPORT DU COMPOSANT BADGE
import UserBadge from './UserBadge';
import { getDisplayBadge } from '../utils/getDisplayBadge';

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false); 
  const timerRef = useRef(null); 
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [authTick, setAuthTick] = useState(0);

  const { disconnectSocket = () => {} } = useSocket() || {};
  const { notifications } = useSocket();

  // ÉCOUTEUR : Login/Logout déclenchent "authChanged"
  useEffect(() => {
    const handleAuthChange = () => setAuthTick(prev => prev + 1);
    window.addEventListener('authChanged', handleAuthChange);
    return () => window.removeEventListener('authChanged', handleAuthChange);
  }, []);

  // 🆕 Lecture avec location.pathname + authTick pour refléter l'état instantanément
  const user = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }, [authTick, location.pathname]);

  const isAuthenticated = useMemo(() => {
    try { return !!localStorage.getItem('token'); } catch { return false; }
  }, [authTick, location.pathname]);

  const isLeader = user?.role === 'leader';

  const unreadCount = useMemo(() => {
    if (!notifications || !Array.isArray(notifications)) return 0;
    return notifications.reduce((count, n) => (n.read === false ? count + 1 : count), 0);
  }, [notifications]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      timerRef.current = setTimeout(() => setIsOpen(false), 8000);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isOpen]);

  const closeMenu = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 600);
  };

  const toggleMenu = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsOpen(!isOpen);
  };

  const navbarClasses = useMemo(() => {
    const base = ['navbar', 'navbar-expand-lg', 'fixed-top', 'transition-all', scrolled ? 'navbar-scrolled' : '', `theme-${theme}`, isOpen ? 'mobile-menu-open' : ''];
    base.push(theme === 'dark' ? 'navbar-dark bg-dark' : 'navbar-light bg-light');
    return base.filter(Boolean).join(' ');
  }, [theme, scrolled, isOpen]);

  const handleOpenLogout = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = useCallback(async () => {
    setShowLogoutModal(false);
    try {
      closeMenu(); 
      disconnectSocket();
      await performLogout();
      window.dispatchEvent(new Event('authChanged'));
      navigate('/', { replace: true });
    } catch (e) { 
      console.error(e); 
    }
  }, [navigate, disconnectSocket]);

  const isActive = useCallback(path => (location.pathname === path ? 'active' : ''), [location.pathname]);

  // 🆕 Badge à afficher
  const displayBadge = getDisplayBadge(user?.userBadges || []);

  const renderAuthenticatedLinks = () => (
    <>
      <li className="nav-item">
        <Link className={`nav-link nav-link-modern ${isActive('/dashboard')}`} to="/dashboard" onClick={closeMenu} title={t('nav_dashboard')}>
          <FontAwesomeIcon icon={faHome} className="nav-icon-modern" />
          <span className="nav-text">{t('nav_dashboard')}</span>
        </Link>
      </li>
      <li className="nav-item">
        <Link className={`nav-link nav-link-modern ${isActive('/profile')}`} to="/profile" onClick={closeMenu} title={t('nav_profile')}>
          <FontAwesomeIcon icon={faUser} className="nav-icon-modern" />
          <span className="nav-text">{t('nav_profile')}</span>
        </Link>
      </li>
      <li className="nav-item">
        <Link className={`nav-link nav-link-modern ${isActive('/leaderboard')}`} to="/leaderboard" onClick={closeMenu} title={t('nav_leaderboard')}>
          <FontAwesomeIcon icon={faTrophy} className="nav-icon-modern" />
          <span className="nav-text">{t('nav_leaderboard')}</span>
        </Link>
      </li>
      
      {/* 🆕 LIEN VERS LE MARCHÉ */}
      <li className="nav-item">
        <Link className={`nav-link nav-link-modern ${isActive('/marketplace')}`} to="/marketplace" onClick={closeMenu} title="Marché">
          <FontAwesomeIcon icon={faStore} className="nav-icon-modern" />
          <span className="nav-text">Marché</span>
        </Link>
      </li>

      {/* 🆕 LIEN VERS LES PRÊTS */}
      <li className="nav-item">
        <Link className={`nav-link nav-link-modern ${isActive('/mes-prets')}`} to="/mes-prets" onClick={closeMenu} title="Mon Prêt">
          <FontAwesomeIcon icon={faHandHoldingUsd} className="nav-icon-modern" />
          <span className="nav-text">Mon Prêt</span>
        </Link>
      </li>

      {/* 🆕 LIEN VERS LES OBLIGATIONS (POINT 16) */}
      <li className="nav-item">
        <Link className={`nav-link nav-link-modern ${isActive('/obligations')}`} to="/obligations" onClick={closeMenu} title="Obligations">
          <FontAwesomeIcon icon={faFileInvoiceDollar} className="nav-icon-modern" />
          <span className="nav-text">Obligations</span>
        </Link>
      </li>

      <li className="nav-item">
        <Link className={`nav-link nav-link-modern ${isActive('/gemini-chat')}`} to="/gemini-chat" onClick={closeMenu} title={t('nav_ai_agent')}>
          <FontAwesomeIcon icon={faRobot} className="nav-icon-modern" />
          <span className="nav-text">{t('nav_ai_agent')}</span>
        </Link>
      </li>
      <li className="nav-item">
        <Link className={`nav-link nav-link-modern ${isActive('/historique')}`} to="/historique" onClick={closeMenu} title="Historique">
          <FontAwesomeIcon icon={faHistory} className="nav-icon-modern" />
          <span className="nav-text">Historique</span>
        </Link>
      </li>

      {isLeader && (
        <li className="nav-item">
          <Link className={`nav-link nav-link-modern ${isActive('/mes-commissions')}`} to="/mes-commissions" onClick={closeMenu} title="Commissions">
            <FontAwesomeIcon icon={faChartLine} className="nav-icon-modern" />
            <span className="nav-text">Commissions</span>
          </Link>
        </li>
      )}

      <li className="nav-item">
        <Link className={`nav-link nav-link-modern nav-notifications ${isActive('/notifications')}`} to="/notifications" onClick={closeMenu} title="Notifications">
          <FontAwesomeIcon icon={faBell} className="nav-icon-modern" />
          <span className="nav-text">Notifications</span>
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </Link>
      </li>

      <li className="nav-item d-lg-none">
        <ThemeToggleButton />
      </li>

      <li className="nav-item">
        <button onClick={handleOpenLogout} className="nav-link nav-link-modern nav-logout-btn" title={t('nav_logout')}>
          <FontAwesomeIcon icon={faSignOutAlt} className="nav-icon-modern" />
          <span className="nav-text">{t('nav_logout')}</span>
        </button>
      </li>
    </>
  );

  const renderUnauthenticatedLinks = () => (
    <>
      <li className="nav-item">
        <Link className={`nav-link nav-link-modern ${isActive('/register')}`} to="/register" onClick={closeMenu}>
          <span className="nav-text">{t('nav_register')}</span>
        </Link>
      </li>
      <li className="nav-item">
        <Link className="btn btn-primary login-btn" to="/" onClick={closeMenu}>
          {t('nav_login')}
        </Link>
      </li>
      <li className="nav-item d-lg-none">
        <ThemeToggleButton />
      </li>
    </>
  );

  return (
    <>
      <nav className={navbarClasses} role="navigation" aria-label="Navigation principale">
        <div className="container-fluid container-lg">
          
          <Link className="navbar-brand fw-bold d-flex align-items-center" to="/dashboard" onClick={closeMenu}>
            <span className="brand-text">INSULATED</span>
          </Link>

          <button
            className={`navbar-toggler custom-toggler ${isOpen ? 'open' : ''}`}
            type="button"
            onClick={toggleMenu}
            aria-expanded={isOpen}
            aria-label="Menu"
          >
            <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
          </button>

          <div className={`collapse navbar-collapse ${isOpen ? 'show' : ''}`} id="navbarNav">
            <ul className="navbar-nav ms-auto align-items-lg-center">
              
              <li className="nav-item me-3 mb-2 mb-lg-0 d-none d-lg-block">
                <div className="d-flex align-items-center"><ThemeToggleButton /></div>
              </li>
              
              {isAuthenticated ? renderAuthenticatedLinks() : renderUnauthenticatedLinks()}

              {/* 🆕 Badge à côté du nom si connecté (dans le menu) */}
              {isAuthenticated && displayBadge && (
                <li className="nav-item d-none d-lg-block ms-2">
                  <UserBadge badge={displayBadge.badge} size="sm" animate={false} />
                </li>
              )}

            </ul>
          </div>
        </div>
      </nav>
      <div className="navbar-spacer" />

      <Modal
        show={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        centered
        className="custom-logout-modal"
        backdrop="static"
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">
            <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" />
          </div>
          <h3 className="logout-title">Déconnexion</h3>
          <p className="logout-text">
            Êtes-vous sûr de vouloir vous déconnecter ? Vous devrez vous reconnecter pour accéder à votre compte.
          </p>
          <div className="logout-actions">
            <button 
              className="logout-btn-cancel" 
              onClick={() => setShowLogoutModal(false)}
            >
              Annuler
            </button>
            <button 
              className="logout-btn-confirm" 
              onClick={handleConfirmLogout}
            >
              Oui, me déconnecter
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Navbar;